// libs
import { History } from 'history'
import * as React from 'react'

// interfaces / constants
import { IAddress } from 'src/interfaces/address'
import { IImageObject } from 'src/interfaces/attachment'
import { IContentItemType } from 'src/interfaces/posts'
import { IEventData } from 'src/post-creation-wizard/wizards/common/interfaces'
import { IUpdatePostTracker, TrackingLabel } from 'src/utils/reporting/events_tracking/events_tracking'

// classes
import Modal from 'src/components/modal/modal'

// helpers
import {
  ActorProfilePreviewFragmentFragment,
  ContentItemFragmentFragment,
  ContentItemQuery,
  OglinkFragmentFragment
} from '@lopo-tech/lopo-api'
import { getLabel, postSubmitTracking } from 'src/components/post/utils'
import { IContentItemMutations } from 'src/high_order_components/with_content_item_mutations'
import DateFormatter from 'src/utils/date_formatter/date_formatter'
import { UrlUtils } from 'src/utils/url/url'

const PostUpdateWizard = React.lazy(
  () => import(/* webpackChunkName: "post-creation-wizard" */'src/post-creation-wizard/wizards/update/update_wizard'))

export interface IMapRouteToProps {
  matchUrl: string;
  history: History;
  path: string;
  search: string;
  startPath?: string;
}

export interface IOwnProps {
  contentItem: NonNullable<ContentItemQuery['contentItem']>;
  onClose: () => void;
  tracking: IUpdatePostTracker;
}

export interface IPostUpdateStoreState {
  loggedInProfileAddress?: IAddress;
  profileType: ActorProfilePreviewFragmentFragment['__typename'];
}

type IPostUpdateProps = IOwnProps & IPostUpdateStoreState & IMapRouteToProps & IContentItemMutations;

interface IState {
  hasError: boolean;
  isLoading: boolean;
  body: string;
  eventData: IEventData;
  title: string;
  oglinkId?: string;
  savedImages: IImageObject[];
  titleImageIndex: number;
  draftedImages: IImageObject[][];
  linkPreviewParams?: OglinkFragmentFragment;
}

export default class PostUpdate extends React.PureComponent<IPostUpdateProps, IState> {
  constructor (props: IPostUpdateProps) {
    super(props)
    this.hideWizard = this.hideWizard.bind(this)
    this.updateArticle = this.updateArticle.bind(this)
    this.updateEvent = this.updateEvent.bind(this)
    this.updateSnapshot = this.updateSnapshot.bind(this)
    this.setBody = this.setBody.bind(this)
    this.setTitle = this.setTitle.bind(this)
    this.setTitleImageIndex = this.setTitleImageIndex.bind(this)
    this.setDraftedImages = this.setDraftedImages.bind(this)
    this.setSavedImages = this.setSavedImages.bind(this)
    this.updateEventData = this.updateEventData.bind(this)
    this.cleanUpImages = this.cleanUpImages.bind(this)
    this.getUpdateData = this.getUpdateData.bind(this)
    this.setEventData = this.setEventData.bind(this)
    this.setLinkPreviewParams = this.setLinkPreviewParams.bind(this)
    this.submitTracking = this.submitTracking.bind(this)

    this.state = {
      hasError: false,
      isLoading: false,
      body: '',
      oglinkId: undefined,
      draftedImages: [],
      eventData: {
        category: undefined,
        endDate: undefined,
        endTime: undefined,
        startDate: undefined,
        startTime: undefined
      },
      linkPreviewParams: undefined,
      savedImages: [],
      title: '',
      titleImageIndex: 0
    }
  }

  public componentDidMount () {
    this.getUpdateData()
  }

  public render () {
    const {
      contentItem,
      history,
      matchUrl,
      search,
      startPath,
      path,
      profileType,
      tracking
    } = this.props

    const {
      body,
      title,
      oglinkId,
      savedImages,
      draftedImages,
      eventData,
      titleImageIndex,
      linkPreviewParams
    } = this.state

    const params = {
      hasError: this.state.hasError,
      isLoading: this.state.isLoading,
      address: contentItem.address,
      author: contentItem.author,
      basePath: matchUrl,
      body,
      cleanUpImages: this.cleanUpImages,
      currentPath: path,
      draftedImages,
      eventData,
      history,
      label: getLabel(profileType),
      linkPreviewParams,
      oglinkId,
      onCancel: this.hideWizard,
      onEnd: this.hideWizard,
      postId: contentItem.id,
      postType: this.mapToOldPostType(contentItem.__typename),
      savedImages,
      search,
      setBody: this.setBody,
      setDraftedImages: this.setDraftedImages,
      setLinkPreviewParams: this.setLinkPreviewParams,
      setSavedImages: this.setSavedImages,
      setTitle: this.setTitle,
      setTitleImageIndex: this.setTitleImageIndex,
      startPath,
      title,
      titleImageIndex,
      tracking,
      updateArticle: this.updateArticle,
      updateEvent: this.updateEvent,
      updateSnapshot: this.updateSnapshot,
      updateEventData: this.updateEventData
    }
    return (
      <Modal onClose={this.hideWizard}>
        <PostUpdateWizard {...params} />
      </Modal>
    )
  }

  private mapToOldPostType (
    newType: ContentItemFragmentFragment['__typename']
  ): IContentItemType {
    switch (newType) {
      case 'Article':
        return 'publicPost'
      case 'Event':
        return 'event'
      case 'Snapshot':
        return 'snapshot'
    }
  }

  private setLinkPreviewParams (linkPreviewParams: OglinkFragmentFragment) {
    this.setState({ linkPreviewParams })
    this.setOglinkId(linkPreviewParams?.id)
  }

  private cleanUpImages () {
    this.setState({ draftedImages: [] })
  }

  private setSavedImages (savedImages: IImageObject[]) {
    this.setState({ savedImages })
  }

  private setDraftedImages (draftedImages: IImageObject[][]) {
    this.setState({ draftedImages })
  }

  private setBody (body: string) {
    this.setState({ body })
  }

  private setTitle (title: string) {
    this.setState({ title })
  }

  private setOglinkId (oglinkId?: string) {
    this.setState({ oglinkId })
  }

  private updateEventData (newEventData: IEventData) {
    this.setState({ eventData: { ...this.state.eventData, ...newEventData } })
  }

  private setTitleImageIndex (index: number) {
    this.setState({ titleImageIndex: index })
  }

  private hideWizard () {
    const { onClose } = this.props
    onClose()
  }

  private getUpdateData () {
    const contentItem = this.props.contentItem

    this.setState({
      body: contentItem.content.text ?? '',
      savedImages: contentItem.attachments.map((attachment) => ({
        src: attachment.file.previewUrl,
        id: attachment.id
      })),
      title: contentItem.title ?? '',
      linkPreviewParams: contentItem.oglink ?? undefined,
      oglinkId: contentItem.oglink?.id ?? ''
    })

    if (contentItem.__typename === 'Event') {
      this.setEventData(contentItem)
    }
  }

  private setEventData (contentItem: NonNullable<ContentItemQuery['contentItem'] & { __typename: 'Event' }>) {
    const category = contentItem.categories[0].id
    const categoryImageUrl = contentItem.categories[0].image?.file.previewUrl

    const endDate = contentItem.endTime ? DateFormatter.dateInDatePickerFormat(contentItem.endTime) : undefined
    const endTime = contentItem.endTime ? DateFormatter.asTime(contentItem.endTime) : undefined
    const startDate = contentItem.startTime ? DateFormatter.dateInDatePickerFormat(contentItem.startTime) : undefined
    const startTime = contentItem.startTime ? DateFormatter.asTime(contentItem.startTime) : undefined

    this.setState({
      eventData: {
        category,
        categoryImageUrl,
        endDate,
        endTime,
        startDate,
        startTime,
        // The edit flow cannot change either of these - `UpdateEventInput` has no
        // field for them, so the stored values are safe - but its preview renders
        // the same card as everywhere else. Without them an online event previews
        // with no link and, since its address comes back empty, with no location
        // row at all.
        format: contentItem.format ?? undefined,
        onlineMeetingUrl: contentItem.onlineMeetingUrl ?? undefined
      }
    })
  }

  private async updateArticle () {
    if (this.props.contentItem.__typename !== 'Article') {
      return
    }

    this.setState({ isLoading: true })

    const id = this.props.contentItem.id
    const commentable = this.props.contentItem.isCommentable

    const assetIds = await this.prepareImages()

    const body = this.state.body
    const title = this.state.title
    const oglinkId = this.state.oglinkId

    const res = await this.props.updateArticle({
      variables: {
        input: {
          assetIds,
          commentable,
          body,
          id,
          oglinkId,
          title
        }
      }
    })

    this.setState({ isLoading: false })

    if (res.data?.updateArticle?.article?.slug) {
      const redirectTo = UrlUtils.detailPage(
        res.data.updateArticle.article.slug,
        'Article'
      )

      setTimeout(() => {
        this.props.history.push(redirectTo)
      }, 500)
    }
  }

  private async updateEvent () {
    if (this.props.contentItem.__typename !== 'Event') {
      return
    }

    this.setState({ isLoading: true })

    const id = this.props.contentItem.id
    const commentable = this.props.contentItem.isCommentable

    const assetIds = await this.prepareImages()

    const body = this.state.body
    const title = this.state.title
    const oglinkId = this.state.oglinkId
    const eventData = this.state.eventData

    // ensure we have what we need before we proceed
    if (
      !eventData.category ||
      !eventData.startTime ||
      !eventData.startDate
    ) {
      // TODO: implement better error handling/state typings here
      return
    }

    const startTime = DateFormatter.toDateTimeISOString(
      eventData.startDate,
      eventData.startTime
    )
    const endTime =
      eventData.endDate && eventData.endTime
        ? DateFormatter.toDateTimeISOString(
          eventData.endDate,
          eventData.endTime
        )
        : undefined

    const res = await this.props.updateEvent({
      variables: {
        input: {
          assetIds,
          body,
          commentable,
          id,
          oglinkId,
          categoryIds: [eventData.category],
          startTime,
          title,
          endTime
        }
      }
    })

    this.setState({ isLoading: false })

    if (res.data?.updateEvent?.event?.slug) {
      const redirectTo = UrlUtils.detailPage(
        res.data.updateEvent.event.slug,
        'Event'
      )

      setTimeout(() => {
        this.props.history.push(redirectTo)
      }, 500)
    }
  }

  private async updateSnapshot () {
    if (this.props.contentItem.__typename !== 'Snapshot') {
      return
    }

    this.setState({ isLoading: true })

    const id = this.props.contentItem.id
    const commentable = this.props.contentItem.isCommentable
    const oglinkId = this.props.contentItem.oglink?.id

    const assetIds = this.props.contentItem.attachments.map(({ id }) => id)

    const body = this.state.body

    const res = await this.props.updateSnapshot({
      variables: {
        input: {
          assetIds,
          commentable,
          body,
          id,
          oglinkId
        }
      }
    })

    this.setState({ isLoading: false })

    if (res.data?.updateSnapshot?.snapshot?.slug) {
      const redirectTo = UrlUtils.detailPage(
        res.data.updateSnapshot.snapshot.slug,
        'Snapshot'
      )

      setTimeout(() => {
        this.props.history.push(redirectTo)
      }, 500)
    }
  }

  private async prepareImages () {
    // upload new images
    // already uploaded images already have an asset id
    const imagesToUpload = this.state.savedImages.filter(({ id }) => !id).map(({ src }) => src)

    const uploadedImageAssetIds = [
      ...this.state.savedImages.map(({ id }) => id).filter((id): id is string => !!id),
      ...await this.props.createAsset(imagesToUpload)
    ]

    return uploadedImageAssetIds
  }

  private submitTracking (label: TrackingLabel) {
    const { contentItem, tracking } = this.props

    postSubmitTracking(contentItem.__typename, label, tracking)
  }
}
